import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { LATEST } from "@/api/report/_models/Report";
import { getInventoryReport } from "@/api/report/getInventoryReport";
import { getProductAvailability } from "@/api/product/getProductAvailability";
import { ReconciliationDispatchSummaryCard } from "@/features/reconciliation/reconcillation-dispatch/ReconciliationDispatchSummaryCard";
import { reconcileInventory } from "./reconcileInventory";
import type { InventoryMatchStatus, InventoryReconcileRow } from "./reconcileInventory";

type MuiColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const STATUS_LABEL: Record<InventoryMatchStatus, string> = {
  matched: "Matched",
  missingInCin7: "Missing in Cin7",
};

const STATUS_COLOR: Record<InventoryMatchStatus, MuiColor> = {
  matched: "success",
  missingInCin7: "error",
};

/** True for a matched row whose report on-hand or available differs from Cin7's. */
const hasDiscrepancy = (row: InventoryReconcileRow): boolean => {
  if (!row.availability) {
    return false;
  }
  const onHandDiffers = row.reportItem.onHandQuantity !== row.availability.OnHand;
  const availableDiffers = row.reportItem.availableToSell !== row.availability.Available;
  return onHandDiffers || availableDiffers;
};

export const ReconciliationInventory = () => {
  const reportQuery = getInventoryReport.useQuery(LATEST);
  const availabilityQuery = getProductAvailability.useQuery();

  const reportItems = reportQuery.data ?? [];
  const availability = availabilityQuery.data ?? [];

  const reconcile = useMemo(
    () => reconcileInventory(reportItems, availability),
    [reportItems, availability],
  );

  const isFetching = reportQuery.isFetching || availabilityQuery.isFetching;

  let error: string | null = null;
  if (reportQuery.isError) {
    error = JSON.stringify(reportQuery.error);
  }
  else if (availabilityQuery.isError) {
    error = JSON.stringify(availabilityQuery.error);
  }

  const ready = reportQuery.data && availabilityQuery.data && !isFetching;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ mb: 3, fontWeight: 700 }}
      >
        Inventory Reconciliation
      </Typography>

      {isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load inventory reconciliation: {error}
        </Alert>
      )}

      {ready && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <ReconciliationDispatchSummaryCard
            label="Report items"
            value={reportItems.length}
          />
          <ReconciliationDispatchSummaryCard
            label="Cin7 availability rows"
            value={availability.length}
          />
          <ReconciliationDispatchSummaryCard
            label="Matched"
            value={reconcile.matchedCount}
          />
          <ReconciliationDispatchSummaryCard
            label="Missing in Cin7"
            value={reconcile.missingInCin7Count}
          />
        </Stack>
      )}

      {ready && (
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 260px)",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table
            size="small"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description / Name</TableCell>
                <TableCell>Warehouse (report)</TableCell>
                <TableCell>Location (Cin7)</TableCell>
                <TableCell align="right">On hand (report)</TableCell>
                <TableCell align="right">On hand (Cin7)</TableCell>
                <TableCell align="right">Available (report)</TableCell>
                <TableCell align="right">Available (Cin7)</TableCell>
                <TableCell align="right">In process (report)</TableCell>
                <TableCell align="right">Allocated (Cin7)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reconcile.rows.map((row, i) => {
                const discrepancy = hasDiscrepancy(row);
                return (
                  <TableRow
                    key={i}
                    sx={{ backgroundColor: discrepancy ? "rgba(237, 108, 2, 0.12)" : undefined }}
                  >
                    <TableCell>{row.sku}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[row.status]}
                        color={STATUS_COLOR[row.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {row.reportItem.description || row.availability?.Name || ""}
                    </TableCell>
                    <TableCell>{row.reportItem.warehouse}</TableCell>
                    <TableCell>{row.availability?.Location ?? "—"}</TableCell>
                    <TableCell align="right">{row.reportItem.onHandQuantity}</TableCell>
                    <TableCell align="right">{row.availability?.OnHand ?? "—"}</TableCell>
                    <TableCell align="right">{row.reportItem.availableToSell}</TableCell>
                    <TableCell align="right">{row.availability?.Available ?? "—"}</TableCell>
                    <TableCell align="right">{row.reportItem.inProcessQuantity}</TableCell>
                    <TableCell align="right">{row.availability?.Allocated ?? "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
